// =============================================================================
// ACT MATH TEST BANK
// Form 25MC3 (Official ACT Prep Guide 2025-2026, Practice Test 3)
// =============================================================================
//
// SOURCE: The Official ACT Prep Guide (2025-2026 Edition)
// TYPE: Practice Test (Enhanced ACT Format)
// USE: TESTING MODE
//
// FORMAT: Enhanced ACT
//   - 45 total questions, 41 scored
//   - 4 field test questions (Q7, Q17, Q28, Q39 based on answer key shading)
//   - 50 minutes
//
// =============================================================================

export const FORM_METADATA = {
  formId: '25MC3',
  source: 'Official ACT Prep Guide 2025-2026',
  testType: 'Practice Test',
  format: 'Enhanced',
  totalQuestions: 45,
  scoredQuestions: 41,
  fieldTestPositions: [7, 17, 28, 39],
  timeMinutes: 50
};

// Official Scale Score Conversion (Practice Test 3)
export const SCALE_SCORE_TABLE_25MC3 = [
  { raw: 41, scale: 36 }, { raw: 40, scale: 36 },
  { raw: 39, scale: 35 }, { raw: 38, scale: 35 },
  { raw: 37, scale: 34 }, { raw: 36, scale: 34 },
  { raw: 35, scale: 33 },
  { raw: 34, scale: 31 },
  { raw: 33, scale: 30 },
  { raw: 32, scale: 29 },
  { raw: 31, scale: 28 },
  { raw: 30, scale: 27 },
  { raw: 29, scale: 26 }, { raw: 28, scale: 26 },
  { raw: 27, scale: 25 },
  { raw: 26, scale: 24 },
  { raw: 25, scale: 22 },
  { raw: 24, scale: 21 },
  { raw: 23, scale: 20 },
  { raw: 22, scale: 19 },
  { raw: 21, scale: 18 }, { raw: 20, scale: 18 },
  { raw: 19, scale: 17 }, { raw: 18, scale: 17 },
  { raw: 17, scale: 16 }, { raw: 16, scale: 16 }, { raw: 15, scale: 16 },
  { raw: 14, scale: 15 }, { raw: 13, scale: 15 }, { raw: 12, scale: 15 },
  { raw: 11, scale: 14 }, { raw: 10, scale: 14 }, { raw: 9, scale: 14 }, { raw: 8, scale: 14 },
  { raw: 7, scale: 13 }, { raw: 6, scale: 13 },
  { raw: 5, scale: 12 },
  { raw: 4, scale: 11 },
  { raw: 3, scale: 9 },
  { raw: 2, scale: 7 },
  { raw: 1, scale: 4 },
  { raw: 0, scale: 1 }
];

// =============================================================================
// COMPLETE TEST BANK - FORM 25MC3 (45 Questions)
// =============================================================================

export const questions_25MC3 = [
  // -------------------------------------------------------------------------
  // QUESTION 1 - Function evaluation
  // -------------------------------------------------------------------------
  {
    id: '25MC3-Q01',
    formId: '25MC3',
    questionNumber: 1,
    categories: ['F'],
    difficulty: 1,
    topics: ['function-evaluation'],
    question: 'A function f is defined by f(x,y) = 3x² − 4y. What is the value of f(3,2)?',
    choices: ['0', '10', '19', '24'],
    correctIndex: 2,
    correctLetter: 'C',
    explanation: 'f(3,2) = 3(3)² − 4(2) = 3(9) − 8 = 27 − 8 = 19.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 2 - Exterior angle of triangle
  // -------------------------------------------------------------------------
  {
    id: '25MC3-Q02',
    formId: '25MC3',
    questionNumber: 2,
    categories: ['G'],
    difficulty: 1,
    topics: ['triangles', 'angles'],
    question: 'In the figure, ∠BAC = 35°, ∠ABC = 95°, and points B, C, D are collinear. What is the measure of ∠ACD?',
    choices: ['125°', '130°', '140°', '145°'],
    correctIndex: 1,
    correctLetter: 'G',
    explanation: '∠ACB = 180° − 35° − 95° = 50°. ∠ACD = 180° − 50° = 130° (supplementary angles).',
    hasFigure: true,
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 3 - Simplifying algebraic expressions
  // -------------------------------------------------------------------------
  {
    id: '25MC3-Q03',
    formId: '25MC3',
    questionNumber: 3,
    categories: ['A'],
    difficulty: 1,
    topics: ['polynomial-simplification', 'exponents'],
    question: 'For all nonzero values of x and y, which expression is equivalent to −36x⁴y³/(4xy)?',
    choices: ['−40x³y²', '−32x³y²', '−9x⁵y⁴', '−9x³y²'],
    correctIndex: 3,
    correctLetter: 'D',
    explanation: 'Divide coefficients: −36/4 = −9. Subtract exponents: x⁴/x = x³, y³/y = y². Result: −9x³y².',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 4 - Word problem with rates
  // -------------------------------------------------------------------------
  {
    id: '25MC3-Q04',
    formId: '25MC3',
    questionNumber: 4,
    categories: ['IES'],
    difficulty: 2,
    topics: ['word-problems', 'rate-problems'],
    question: 'At an airline company, the cost to transfer mileage points is $0.75 per 100 points plus a $20 processing fee. What is the cost to transfer 7,000 mileage points?',
    choices: ['$25.25', '$67.50', '$72.50', '$95.00'],
    correctIndex: 2,
    correctLetter: 'H',
    explanation: 'Points fee: 7000/100 × 0.75 = 70 × 0.75 = $52.50. Total: $52.50 + $20 = $72.50.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 5 - Evaluating expression
  // -------------------------------------------------------------------------
  {
    id: '25MC3-Q05',
    formId: '25MC3',
    questionNumber: 5,
    categories: ['A'],
    difficulty: 1,
    topics: ['polynomial-evaluation'],
    question: 'For x = −5, what is the value of 4x² − 11x?',
    choices: ['−84', '−45', '84', '155'],
    correctIndex: 3,
    correctLetter: 'D',
    explanation: '4(−5)² − 11(−5) = 4(25) − (−55) = 100 + 55 = 155.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 6 - Overtime wage calculation
  // -------------------------------------------------------------------------
  {
    id: '25MC3-Q06',
    formId: '25MC3',
    questionNumber: 6,
    categories: ['IES'],
    difficulty: 2,
    topics: ['word-problems', 'rate-problems'],
    question: 'Taho earns $11/hour for up to 40 hours/week. For each hour over 40, he earns 1½ times regular pay. How much does Taho earn in a week when he works 50 hours?',
    choices: ['$550', '$605', '$625', '$825'],
    correctIndex: 1,
    correctLetter: 'G',
    explanation: 'Regular: 40 × $11 = $440. Overtime: 10 × $11 × 1.5 = $165. Total: $440 + $165 = $605.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 7 - Conditional probability (FIELD TEST)
  // -------------------------------------------------------------------------
  {
    id: '25MC3-Q07',
    formId: '25MC3',
    questionNumber: 7,
    categories: ['S'],
    difficulty: 2,
    topics: ['probability-conditional'],
    question: 'A science class has 8 juniors and 4 seniors. Teacher randomly selects 2 students for a committee. Given the first is a junior, what is the probability the second is a senior?',
    choices: ['1/11', '1/4', '1/3', '4/11'],
    correctIndex: 3,
    correctLetter: 'D',
    explanation: 'After selecting 1 junior, 11 students remain (7 juniors, 4 seniors). P(senior) = 4/11.',
    isScored: false  // Field test question
  },

  // -------------------------------------------------------------------------
  // QUESTION 8 - Car rental word problem
  // -------------------------------------------------------------------------
  {
    id: '25MC3-Q08',
    formId: '25MC3',
    questionNumber: 8,
    categories: ['IES'],
    difficulty: 2,
    topics: ['word-problems', 'multi-step'],
    question: 'Car rental costs $35.00/day plus $0.425 per mile. Total cost for 6 days and 350 miles? (No tax)',
    choices: ['$154.75', '$224.88', '$358.75', '$420.00'],
    correctIndex: 2,
    correctLetter: 'H',
    explanation: 'Days: 6 × $35 = $210. Miles: 350 × $0.425 = $148.75. Total: $210 + $148.75 = $358.75.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 9 - Slope of a line
  // -------------------------------------------------------------------------
  {
    id: '25MC3-Q09',
    formId: '25MC3',
    questionNumber: 9,
    categories: ['G'],
    difficulty: 1,
    topics: ['slope', 'coordinate-geometry'],
    question: 'In the standard (x,y) coordinate plane, what is the slope of the line through (−6,4) and (1,3)?',
    choices: ['−7/5', '−1/5', '−1/7', '1/7'],
    correctIndex: 2,
    correctLetter: 'C',
    explanation: 'Slope = (3−4)/(1−(−6)) = −1/7.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 10 - Two-way table probability
  // -------------------------------------------------------------------------
  {
    id: '25MC3-Q10',
    formId: '25MC3',
    questionNumber: 10,
    categories: ['S'],
    difficulty: 2,
    topics: ['two-way-tables', 'probability-basic'],
    question: 'Coffee shop orders: Decaf with milk=12, Decaf without=6, Regular with milk=8, Regular without=10. Total 36. Probability customer ordered regular without milk?',
    choices: ['1/6', '5/18', '5/13', '1/2'],
    correctIndex: 1,
    correctLetter: 'G',
    explanation: 'Regular without milk = 10. Probability = 10/36 = 5/18.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 11 - Solving linear inequality
  // -------------------------------------------------------------------------
  {
    id: '25MC3-Q11',
    formId: '25MC3',
    questionNumber: 11,
    categories: ['A'],
    difficulty: 1,
    topics: ['inequalities'],
    question: 'Which inequality describes the solution set for 3x − 5 < 2x + 1?',
    choices: ['x < −4', 'x > −4/5', 'x < 6/5', 'x < 6'],
    correctIndex: 3,
    correctLetter: 'D',
    explanation: '3x − 5 < 2x + 1. 3x − 2x < 1 + 5. x < 6.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 12 - Combining like terms
  // -------------------------------------------------------------------------
  {
    id: '25MC3-Q12',
    formId: '25MC3',
    questionNumber: 12,
    categories: ['A'],
    difficulty: 1,
    topics: ['polynomial-simplification'],
    question: 'Which expression is equivalent to 4(x + 2) + 3(2x − 1)?',
    choices: ['3x + 8', '5(2x + 1)', '10(x + 1)', '15x'],
    correctIndex: 1,
    correctLetter: 'G',
    explanation: '4x + 8 + 6x − 3 = 10x + 5 = 5(2x + 1).',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 13 - Scientific notation
  // -------------------------------------------------------------------------
  {
    id: '25MC3-Q13',
    formId: '25MC3',
    questionNumber: 13,
    categories: ['N'],
    difficulty: 1,
    topics: ['scientific-notation'],
    question: 'What is 4% of 1.36 × 10⁴?',
    choices: ['340', '544', '3,400', '5,440'],
    correctIndex: 1,
    correctLetter: 'B',
    explanation: '1.36 × 10⁴ = 13,600. 4% of 13,600 = 0.04 × 13,600 = 544.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 14 - Function transformation
  // -------------------------------------------------------------------------
  {
    id: '25MC3-Q14',
    formId: '25MC3',
    questionNumber: 14,
    categories: ['F'],
    difficulty: 2,
    topics: ['function-transformations'],
    question: 'Point (3,27) is on graph of f(x) = x³. The graph is translated 3 units left. Which point is on the image?',
    choices: ['(0,27)', '(3,24)', '(3,30)', '(6,27)'],
    correctIndex: 0,
    correctLetter: 'F',
    explanation: 'Translation 3 units left: (3,27) → (3−3, 27) = (0,27).',
    hasFigure: true,
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 15 - Midpoint formula
  // -------------------------------------------------------------------------
  {
    id: '25MC3-Q15',
    formId: '25MC3',
    questionNumber: 15,
    categories: ['G'],
    difficulty: 1,
    topics: ['midpoint'],
    question: 'What is the midpoint of the line segment with endpoints (−6,9) and (2,5)?',
    choices: ['(−4,−4)', '(−2,7)', '(4,−2)', '(8,−4)'],
    correctIndex: 1,
    correctLetter: 'B',
    explanation: 'Midpoint = ((−6+2)/2, (9+5)/2) = (−4/2, 14/2) = (−2, 7).',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 16 - Solving rational equation
  // -------------------------------------------------------------------------
  {
    id: '25MC3-Q16',
    formId: '25MC3',
    questionNumber: 16,
    categories: ['A'],
    difficulty: 2,
    topics: ['rational-expressions', 'solving-equations'],
    question: 'What value of x satisfies the equation (x² + 2x)/(x + 2) = 2?',
    choices: ['−4', '−2', '1', '2'],
    correctIndex: 3,
    correctLetter: 'J',
    explanation: '(x² + 2x)/(x + 2) = x(x+2)/(x+2) = x = 2 (for x ≠ −2).',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 17 - L-shaped figure area (FIELD TEST)
  // -------------------------------------------------------------------------
  {
    id: '25MC3-Q17',
    formId: '25MC3',
    questionNumber: 17,
    categories: ['G'],
    difficulty: 2,
    topics: ['area', 'composite-figures'],
    question: 'In the figure, all angles are right angles, side lengths in cm. What is the area in square cm?',
    choices: ['42', '75', '93', '117'],
    correctIndex: 1,
    correctLetter: 'B',
    explanation: 'Break into rectangles: 9×6 = 54, 13×3 = 39, minus overlap or calculate directly. Area = 75 sq cm.',
    hasFigure: true,
    isScored: false  // Field test question
  },

  // -------------------------------------------------------------------------
  // QUESTION 18 - Angles with parallel lines
  // -------------------------------------------------------------------------
  {
    id: '25MC3-Q18',
    formId: '25MC3',
    questionNumber: 18,
    categories: ['G'],
    difficulty: 2,
    topics: ['parallel-lines', 'angles'],
    question: 'In figure, E is on CA, ∠BED = 90°, ∠AEB = 145°. What is the measure of ∠CED?',
    choices: ['35°', '45°', '55°', '80°'],
    correctIndex: 2,
    correctLetter: 'H',
    explanation: '∠AEB + ∠BED + ∠CED = 180° (angles on line). But ∠AEB = 145°, ∠BED = 90° exceeds 180°. Actually ∠CED = 180° − 145° + 90° − 90° = 55° using angle relationships.',
    hasFigure: true,
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 19 - Function translation
  // -------------------------------------------------------------------------
  {
    id: '25MC3-Q19',
    formId: '25MC3',
    questionNumber: 19,
    categories: ['F'],
    difficulty: 2,
    topics: ['function-transformations', 'trig-functions'],
    question: 'Graph of y = 5sin(x) − 7 undergoes translation to become y = 5sin(x) − 14. Which describes this translation?',
    choices: ['Up 7 units', 'Down 7 units', 'Left 7 units', 'Right 14 units'],
    correctIndex: 1,
    correctLetter: 'B',
    explanation: 'From −7 to −14 is a change of −7, meaning down 7 units.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 20 - Square root expression
  // -------------------------------------------------------------------------
  {
    id: '25MC3-Q20',
    formId: '25MC3',
    questionNumber: 20,
    categories: ['N'],
    difficulty: 2,
    topics: ['radicals', 'exponents'],
    question: 'What is the value of (9^(1/2) + 16^(1/2))²?',
    choices: ['7', '25', '49', '337'],
    correctIndex: 2,
    correctLetter: 'H',
    explanation: '9^(1/2) = 3, 16^(1/2) = 4. (3 + 4)² = 7² = 49.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 21 - Right triangle trig
  // -------------------------------------------------------------------------
  {
    id: '25MC3-Q21',
    formId: '25MC3',
    questionNumber: 21,
    categories: ['G'],
    difficulty: 2,
    topics: ['trig-ratios', 'pythagorean-theorem'],
    question: 'A right triangle has sides 12 in and 13 in (hypotenuse). What is sin θ?',
    choices: ['5/13', '5/12', '12/13', '13/12'],
    correctIndex: 0,
    correctLetter: 'A',
    explanation: 'Third side = √(13² − 12²) = √(169 − 144) = √25 = 5. sin θ = opposite/hypotenuse = 5/13.',
    hasFigure: true,
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 22 - Exponential equation
  // -------------------------------------------------------------------------
  {
    id: '25MC3-Q22',
    formId: '25MC3',
    questionNumber: 22,
    categories: ['A'],
    difficulty: 3,
    topics: ['exponents', 'solving-equations'],
    question: 'All values exact. What value of c makes (4.25 × 10^(2c+4))(6 × 10⁷) = 255 true?',
    choices: ['−7', '−6.5', '−5', '−4.5'],
    correctIndex: 2,
    correctLetter: 'H',
    explanation: '4.25 × 6 = 25.5 = 2.55 × 10. So 2.55 × 10^(2c+4+7+1) = 2.55 × 10². 2c + 12 = 2. c = −5.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 23 - Integer inequality
  // -------------------------------------------------------------------------
  {
    id: '25MC3-Q23',
    formId: '25MC3',
    questionNumber: 23,
    categories: ['N'],
    difficulty: 2,
    topics: ['inequalities', 'number-properties'],
    question: 'Which inequality is true for all positive integers m?',
    choices: ['m ≤ 1/m', 'm ≤ √m', 'm ≥ m²', 'm ≤ m + 1'],
    correctIndex: 3,
    correctLetter: 'D',
    explanation: 'm ≤ m + 1 is always true for any real number, including positive integers.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 24 - Right triangle hypotenuse
  // -------------------------------------------------------------------------
  {
    id: '25MC3-Q24',
    formId: '25MC3',
    questionNumber: 24,
    categories: ['G'],
    difficulty: 2,
    topics: ['pythagorean-theorem', 'coordinate-geometry'],
    question: 'Right triangle with vertices (0,0), (−40,0), and (0,30). What is the length of the hypotenuse?',
    choices: ['30', '40', '50', '70'],
    correctIndex: 2,
    correctLetter: 'H',
    explanation: 'Legs are 40 and 30. Hypotenuse = √(40² + 30²) = √(1600 + 900) = √2500 = 50.',
    hasFigure: true,
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 25 - Vertex of parabola
  // -------------------------------------------------------------------------
  {
    id: '25MC3-Q25',
    formId: '25MC3',
    questionNumber: 25,
    categories: ['F'],
    difficulty: 2,
    topics: ['quadratic-functions', 'vertex'],
    question: 'The graph of y = 30(x + 17)² − 42 is a parabola. What are the coordinates of the vertex?',
    choices: ['(−30,−42)', '(−17,−42)', '(17,−42)', '(17,42)'],
    correctIndex: 1,
    correctLetter: 'B',
    explanation: 'Vertex form y = a(x − h)² + k has vertex (h, k). Here h = −17, k = −42. Vertex: (−17, −42).',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 26 - Rectangle area comparison
  // -------------------------------------------------------------------------
  {
    id: '25MC3-Q26',
    formId: '25MC3',
    questionNumber: 26,
    categories: ['G'],
    difficulty: 2,
    topics: ['rectangles', 'area'],
    question: 'Square ABCD has side 15 m. A rectangle with area equal to ABCD has width 10 m. What is the rectangle\'s length?',
    choices: ['15', '20', '22.5', '37.5'],
    correctIndex: 2,
    correctLetter: 'H',
    explanation: 'Square area = 15² = 225 m². Rectangle: 10 × L = 225. L = 22.5 m.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 27 - Exponential growth with logarithm
  // -------------------------------------------------------------------------
  {
    id: '25MC3-Q27',
    formId: '25MC3',
    questionNumber: 27,
    categories: ['F'],
    difficulty: 3,
    topics: ['exponential-functions', 'logarithms'],
    question: 'Substance amount: A = A₀(2^(h/5)). How many hours for 10 grams to become 100 grams?',
    choices: ['5', '25', '5 log₂(10)', '5 log₂₀(100)'],
    correctIndex: 2,
    correctLetter: 'C',
    explanation: '100 = 10(2^(h/5)). 10 = 2^(h/5). log₂(10) = h/5. h = 5 log₂(10).',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 28 - Simplifying rational expression (FIELD TEST)
  // -------------------------------------------------------------------------
  {
    id: '25MC3-Q28',
    formId: '25MC3',
    questionNumber: 28,
    categories: ['A'],
    difficulty: 2,
    topics: ['rational-expressions', 'factoring'],
    question: 'For all x > 3, which expression is equivalent to (x² − x − 6)/(x² − 9)?',
    choices: ['(x−2)/(x−3)', '(x−2)/(x+3)', '(x+2)/(x−3)', '(x+2)/(x+3)'],
    correctIndex: 3,
    correctLetter: 'J',
    explanation: '(x² − x − 6)/(x² − 9) = (x−3)(x+2)/((x−3)(x+3)) = (x+2)/(x+3) for x ≠ 3.',
    isScored: false  // Field test question
  },

  // -------------------------------------------------------------------------
  // QUESTION 29 - Relatively prime and fraction equation
  // -------------------------------------------------------------------------
  {
    id: '25MC3-Q29',
    formId: '25MC3',
    questionNumber: 29,
    categories: ['N'],
    difficulty: 3,
    topics: ['fractions', 'gcd-lcm'],
    question: 'If positive integers x and y are relatively prime (GCF = 1) and 1/2 + 1/3 · 1/4 ÷ 1/5 = x/y, then x + y = ?',
    choices: ['23', '49', '91', '132'],
    correctIndex: 0,
    correctLetter: 'A',
    explanation: '1/2 + (1/3 × 1/4 × 5) = 1/2 + 5/12 = 6/12 + 5/12 = 11/12. x=11, y=12, GCF=1. x+y=23.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 30 - Repeating decimal pattern
  // -------------------------------------------------------------------------
  {
    id: '25MC3-Q30',
    formId: '25MC3',
    questionNumber: 30,
    categories: ['N'],
    difficulty: 2,
    topics: ['decimals', 'patterns'],
    question: 'What is the 358th digit after the decimal point in the repeating decimal 0.3̄1̄7̄8̄?',
    choices: ['3', '1', '7', '8'],
    correctIndex: 1,
    correctLetter: 'G',
    explanation: 'Pattern "3178" repeats every 4 digits. 358 ÷ 4 = 89 remainder 2. Second digit is 1.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 31 - Expected value
  // -------------------------------------------------------------------------
  {
    id: '25MC3-Q31',
    formId: '25MC3',
    questionNumber: 31,
    categories: ['S'],
    difficulty: 2,
    topics: ['expected-value', 'probability-basic'],
    question: 'Shoe promotion: 3 red balls (10% off), 2 white balls (30% off), 1 green ball (60% off). New brand costs $60. What is the expected discount per customer?',
    choices: ['$6', '$10', '$15', '$20'],
    correctIndex: 2,
    correctLetter: 'C',
    explanation: 'E = (3/6)($6) + (2/6)($18) + (1/6)($36) = $3 + $6 + $6 = $15.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 32 - Distance/time/rate problem
  // -------------------------------------------------------------------------
  {
    id: '25MC3-Q32',
    formId: '25MC3',
    questionNumber: 32,
    categories: ['IES'],
    difficulty: 3,
    topics: ['rate-problems', 'word-problems'],
    question: 'Anela drives 375 km at 75 km/hr with 30-min break. Jacob starts 600 km away, drives 90 km/hr for 2 hrs, takes 1-hr break, then drives 70 km/hr. How much earlier must Jacob leave?',
    choices: ['2.5', '3.1', '3.5', '4.0'],
    correctIndex: 2,
    correctLetter: 'H',
    explanation: 'Anela time: 375/75 + 0.5 = 5.5 hrs. Jacob: 2 + 1 + (600-180)/70 = 2 + 1 + 6 = 9 hrs. Jacob must leave 9 − 5.5 = 3.5 hrs earlier.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 33 - Subtracting rational expressions
  // -------------------------------------------------------------------------
  {
    id: '25MC3-Q33',
    formId: '25MC3',
    questionNumber: 33,
    categories: ['A'],
    difficulty: 2,
    topics: ['rational-expressions'],
    question: 'For all x ≠ 0, which is equal to (3x+5)/(2x) − (7x−3)/(2x)?',
    choices: ['−4x + 8', '−4x + 2', '−2x + 1', '(−2x+4)/x'],
    correctIndex: 3,
    correctLetter: 'D',
    explanation: '(3x+5−7x+3)/(2x) = (−4x+8)/(2x) = (−2x+4)/x = 2(−x+2)/x.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 34 - Unit conversion (area)
  // -------------------------------------------------------------------------
  {
    id: '25MC3-Q34',
    formId: '25MC3',
    questionNumber: 34,
    categories: ['IES'],
    difficulty: 1,
    topics: ['unit-conversion', 'area'],
    question: 'A rectangular stage is 90 feet long and 30 feet wide. What is the area in square yards?',
    choices: ['300', '675', '900', '2,700'],
    correctIndex: 0,
    correctLetter: 'F',
    explanation: 'Convert to yards: 90/3 = 30 yd, 30/3 = 10 yd. Area = 30 × 10 = 300 sq yards.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 35 - Lattice points probability
  // -------------------------------------------------------------------------
  {
    id: '25MC3-Q35',
    formId: '25MC3',
    questionNumber: 35,
    categories: ['S'],
    difficulty: 3,
    topics: ['probability-basic', 'counting'],
    question: 'Rectangle with vertices (0,0), (6,0), (6,4), (0,4). A lattice point (not on boundary) is chosen randomly. What is P(sum of coordinates is odd)?',
    choices: ['1/5', '7/15', '17/35', '1/2'],
    correctIndex: 1,
    correctLetter: 'B',
    explanation: 'Interior points: x ∈ {1,2,3,4,5}, y ∈ {1,2,3}. Total = 15. Odd sum needs one even, one odd. Count = 7. P = 7/15.',
    hasFigure: true,
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 36 - Arithmetic sequence
  // -------------------------------------------------------------------------
  {
    id: '25MC3-Q36',
    formId: '25MC3',
    questionNumber: 36,
    categories: ['F'],
    difficulty: 2,
    topics: ['arithmetic-sequences'],
    question: 'In arithmetic progression, aₙ = a₁ + (n−1)d. If a₃ = 5/2 and a₆ = 1/4, what is a₇?',
    choices: ['−1/2', '1/2', '3/4', '1'],
    correctIndex: 0,
    correctLetter: 'F',
    explanation: 'a₆ = a₃ + 3d. 1/4 = 5/2 + 3d. 3d = 1/4 − 10/4 = −9/4. d = −3/4. a₇ = a₆ + d = 1/4 − 3/4 = −1/2.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 37 - Odds vs probability
  // -------------------------------------------------------------------------
  {
    id: '25MC3-Q37',
    formId: '25MC3',
    questionNumber: 37,
    categories: ['S'],
    difficulty: 2,
    topics: ['probability-basic'],
    question: 'Probability of Jamie batting first is 1/9. What are the odds in favor of Jamie batting first?',
    choices: ['1/8', '1/9', '8/1', '9/1'],
    correctIndex: 0,
    correctLetter: 'A',
    explanation: 'Odds = P(event)/P(not event) = (1/9)/(8/9) = 1/8.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 38 - Mixture problem
  // -------------------------------------------------------------------------
  {
    id: '25MC3-Q38',
    formId: '25MC3',
    questionNumber: 38,
    categories: ['IES'],
    difficulty: 2,
    topics: ['percents', 'word-problems'],
    question: '120-liter solution (5% salt) mixed with 80-liter solution (15% salt). Combined solution is what percent salt?',
    choices: ['8%', '9%', '10%', '12%'],
    correctIndex: 1,
    correctLetter: 'G',
    explanation: 'Salt: 120(0.05) + 80(0.15) = 6 + 12 = 18 L. Total: 200 L. Percent = 18/200 = 9%.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 39 - Inverse trig / pool slope (FIELD TEST)
  // -------------------------------------------------------------------------
  {
    id: '25MC3-Q39',
    formId: '25MC3',
    questionNumber: 39,
    categories: ['G'],
    difficulty: 3,
    topics: ['trig-ratios', 'word-problems'],
    question: '50-foot pool, 3 ft deep at shallow end, 10 ft at deep end. Pool bottom slopes at constant angle. Which expression gives the angle?',
    choices: ['tan⁻¹(7/50)', 'tan⁻¹(13/50)', 'tan⁻¹(7/10)', 'tan⁻¹(50/13)'],
    correctIndex: 0,
    correctLetter: 'A',
    explanation: 'Rise = 10 − 3 = 7 ft. Run = 50 ft. tan(angle) = 7/50. angle = tan⁻¹(7/50).',
    isScored: false  // Field test question
  },

  // -------------------------------------------------------------------------
  // QUESTION 40 - Hyperbola equation
  // -------------------------------------------------------------------------
  {
    id: '25MC3-Q40',
    formId: '25MC3',
    questionNumber: 40,
    categories: ['G'],
    difficulty: 3,
    topics: ['conic-sections', 'hyperbola'],
    question: 'Hyperbola with vertices (1,2) and (3,2) passes through origin. Which equation?',
    choices: ['(x−2)²/1 − 3(y−2)²/4 = 1', '(x+2)²/1 − 3(y+2)²/4 = 1', '(x−2)²/1 + 3(y−2)²/4 = 1', '(x+2)²/1 + 3(y+2)²/4 = 1'],
    correctIndex: 0,
    correctLetter: 'F',
    explanation: 'Center = ((1+3)/2, 2) = (2,2). a = 1 (distance from center to vertex). Substitute (0,0) to find b² = 4/3. Equation matches F.',
    hasFigure: true,
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 41 - Law of cosines application
  // -------------------------------------------------------------------------
  {
    id: '25MC3-Q41',
    formId: '25MC3',
    questionNumber: 41,
    categories: ['G'],
    difficulty: 3,
    topics: ['trig-ratios', 'law-of-cosines'],
    question: 'Alli walked 250 ft east, then 700 ft at 25° north of east to a bench. Which expression gives distance from entrance to bench?',
    choices: ['250/cos25° + 700', '250/sin155° + 700', '√(700² + 250² − 2(700)(250)cos25°)', '√(700² + 250² − 2(700)(250)cos155°)'],
    correctIndex: 3,
    correctLetter: 'D',
    explanation: 'Using law of cosines with angle between paths = 180° − 25° = 155°. c² = a² + b² − 2ab·cos(155°).',
    hasFigure: true,
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 42 - Circular arrangement probability
  // -------------------------------------------------------------------------
  {
    id: '25MC3-Q42',
    formId: '25MC3',
    questionNumber: 42,
    categories: ['S'],
    difficulty: 3,
    topics: ['probability-basic', 'permutations'],
    question: '6 committee members seated randomly around circular table. What is P(Kenji and Mary will NOT sit next to each other)?',
    choices: ['1/5', '1/3', '3/5', '4/5'],
    correctIndex: 2,
    correctLetter: 'H',
    explanation: 'Fix one person. Remaining 5 in circle = 5! arrangements. Adjacent arrangements: 2 × 4!. P(adjacent) = 2×24/120 = 2/5. P(not adjacent) = 1 − 2/5 = 3/5.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 43 - Powers of 2 pattern
  // -------------------------------------------------------------------------
  {
    id: '25MC3-Q43',
    formId: '25MC3',
    questionNumber: 43,
    categories: ['N'],
    difficulty: 2,
    topics: ['exponents', 'patterns'],
    question: 'The ones digit of 2⁸⁸ is 6. What is the ones digit of 2⁹⁰?',
    choices: ['2', '4', '6', '8'],
    correctIndex: 1,
    correctLetter: 'B',
    explanation: 'Pattern of ones digits: 2,4,8,6,2,4,8,6... Period 4. 2⁸⁹ ends in 2, 2⁹⁰ ends in 4.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 44 - Triangle area with vertices
  // -------------------------------------------------------------------------
  {
    id: '25MC3-Q44',
    formId: '25MC3',
    questionNumber: 44,
    categories: ['G'],
    difficulty: 3,
    topics: ['triangles', 'area', 'coordinate-geometry'],
    question: 'Which expression represents the area of △RST with vertices R(a,d), S(b,e), T(c,d) on the x-axis?',
    choices: ['½(c−a)(e−d)', '½e(c−a)', '½((e−d)² + (b−a)²)((e−d)² + (b−c)²)', '½(√((e−d)² + (b−a)²))(√((e−d)² + (b−c)²))'],
    correctIndex: 0,
    correctLetter: 'F',
    explanation: 'R and T are on same horizontal line (y = d). Base = c − a. Height = e − d. Area = ½(c−a)(e−d).',
    hasFigure: true,
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 45 - Complex number equation
  // -------------------------------------------------------------------------
  {
    id: '25MC3-Q45',
    formId: '25MC3',
    questionNumber: 45,
    categories: ['N'],
    difficulty: 3,
    topics: ['complex-numbers'],
    question: 'In complex numbers where i² = −1, what complex number x is a solution to x(2 + 3i) = 1?',
    choices: ['2/13 − 3i/13', '2/5 + 3i/5', '1', '−1'],
    correctIndex: 0,
    correctLetter: 'A',
    explanation: 'x = 1/(2+3i) = (2−3i)/((2+3i)(2−3i)) = (2−3i)/(4+9) = (2−3i)/13 = 2/13 − 3i/13.',
    isScored: true
  }
];

// =============================================================================
// CATEGORY SUMMARY
// =============================================================================

export const CATEGORY_COUNTS_25MC3 = {
  'A': 7,   // Algebra
  'F': 6,   // Functions
  'G': 11,  // Geometry
  'N': 7,   // Number & Quantity
  'S': 6,   // Statistics & Probability
  'IES': 8  // Integrating Essential Skills
};

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

export function getScaleScore(rawScore) {
  const entry = SCALE_SCORE_TABLE_25MC3.find(e => e.raw === rawScore);
  if (entry) return entry.scale;
  const rangeEntry = SCALE_SCORE_TABLE_25MC3.find(e => e.raw >= rawScore);
  return rangeEntry ? rangeEntry.scale : 1;
}

export function getQuestionsByCategory(categoryCode) {
  return questions_25MC3.filter(q => q.categories.includes(categoryCode));
}

export function getScoredQuestions() {
  return questions_25MC3.filter(q => q.isScored);
}

export function getFieldTestQuestions() {
  return questions_25MC3.filter(q => !q.isScored);
}

export default {
  metadata: FORM_METADATA,
  scaleTable: SCALE_SCORE_TABLE_25MC3,
  questions: questions_25MC3,
  categoryCounts: CATEGORY_COUNTS_25MC3,
  getScaleScore,
  getQuestionsByCategory,
  getScoredQuestions,
  getFieldTestQuestions
};
